import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Team, TeamInvite, TeamMember } from '../types/api';

interface TeamWithMembers extends Team {
  users?: TeamMember[];
}

const Teams = () => {
  const [teams, setTeams] = useState<Team[]>([]);
  const [teamName, setTeamName] = useState('');
  const [success, setSuccess] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteTeamId, setInviteTeamId] = useState('');
  const [invites, setInvites] = useState<TeamInvite[]>([]);
  const [inviteMessage, setInviteMessage] = useState('');
  const [selectedTeam, setSelectedTeam] = useState<TeamWithMembers | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const fetchTeamDetails = async (teamId: string) => {
    try {
      const response = await api.get(`/teams/${teamId}`);
      setSelectedTeam(response.data);
      setIsModalOpen(true);
    } catch (error) {
      console.error(error);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedTeam(null);
  };

  useEffect(() => {
    const fetchTeams = async () => {
      try {
        const response = await api.get('/teams/mine');
        setTeams(response.data);
      } catch (error) {
        console.error(error);
      }
    };

    const fetchInvites = async () => {
      try {
        const response = await api.get('/teams/invites');
        setInvites(response.data || []);
      } catch (error) {
        console.error(error);
      }
    };

    fetchTeams();
    fetchInvites();
  }, []);

  const handleCreateTeam = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await api.post('/teams', { name: teamName });
      setTeams([...teams, response.data]);
      setSuccess(`Tým "${teamName}" byl úspěšně vytvořen!`);
      setTeamName('');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error(error);
    }
  };

  const refreshTeamsAndInvites = async () => {
    try {
      const [teamsRes, invitesRes] = await Promise.all([
        api.get('/teams/mine'),
        api.get('/teams/invites')
      ]);
      setTeams(teamsRes.data || []);
      setInvites(invitesRes.data || []);
    } catch (error) {
      console.error(error);
    }
  };

  const handleSendInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteTeamId) {
      setInviteMessage('Vyberte prosím tým, ze kterého chcete zvát');
      return;
    }
    try {
      await api.post(`/teams/${inviteTeamId}/invite`, { email: inviteEmail });
      await refreshTeamsAndInvites();
      setInviteMessage('Pozvánka byla úspěšně odeslána!');
      setInviteEmail('');
      setInviteTeamId('');
      setTimeout(() => setInviteMessage(''), 3000);
    } catch (error: any) {
      const msg = error.response?.data?.message || 'Pozvánku se nepodařilo odeslat';
      setInviteMessage(msg);
      console.error(error);
    }
  };

  const handleAcceptInvite = async (inviteId: string) => {
    try {
      await api.post(`/teams/invites/${inviteId}/accept`);
      refreshTeamsAndInvites();
    } catch (error) {
      console.error(error);
    }
  };

  const handleDeclineInvite = async (inviteId: string) => {
    try {
      await api.post(`/teams/invites/${inviteId}/decline`);
      refreshTeamsAndInvites();
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-secondary-50 to-accent-50 p-4 sm:p-6 lg:p-8">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8 sm:mb-12">
          <h2 className="text-4xl sm:text-5xl font-bold bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent mb-2">
            Vaše týmy
          </h2>
          <p className="text-gray-600">Zakládejte a spravujte své týmy stolního fotbalu</p>
        </div>

        {success && (
          <div className="mb-6 p-4 text-center text-green-700 bg-green-50 rounded-2xl border border-green-200">
            {success}
          </div>
        )}

        <div className="bg-white/80 backdrop-blur-lg p-6 sm:p-8 rounded-3xl shadow-card border border-white mb-8">
          <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 flex items-center">
            Vytvořit nový tým
          </h3>
          <form onSubmit={handleCreateTeam} className="space-y-6">
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-3">
                Název týmu
              </label>
              <input
                type="text"
                required
                className="w-full p-4 text-gray-900 text-lg bg-white rounded-2xl border-2 border-gray-200 focus:outline-none focus:border-primary-500 focus:ring-4 focus:ring-primary-100 transition-all duration-200"
                placeholder="Zadejte název týmu..."
                value={teamName}
                onChange={(e) => setTeamName(e.target.value)}
              />
            </div>
            <button
              type="submit"
              className="w-full py-4 font-bold text-white text-lg bg-gradient-to-r from-primary-500 to-secondary-500 rounded-2xl shadow-lg hover:shadow-glow transform transition-all duration-200"
            >
              Vytvořit tým
            </button>
          </form>
        </div>

        {teams.length > 0 && (
          <div className="bg-white/80 backdrop-blur-lg p-6 sm:p-8 rounded-3xl shadow-card border border-white mb-8">
            <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 flex items-center">
              Pozvěte hráče do svého týmu
            </h3>
            {inviteMessage && (
              <div className="mb-4 p-3 rounded-xl text-sm text-center bg-primary-50 text-primary-700">
                {inviteMessage}
              </div>
            )}
            <form onSubmit={handleSendInvite} className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-3">Vyberte tým</label>
                <select
                  value={inviteTeamId}
                  onChange={(e) => setInviteTeamId(e.target.value)}
                  className="w-full p-4 bg-white rounded-2xl border-2 border-gray-200 focus:outline-none focus:border-primary-500 focus:ring-4 focus:ring-primary-100 transition-all"
                  required
                >
                  <option value="">Zvolte svůj tým...</option>
                  {teams.map((team) => (
                    <option key={team.id} value={team.id}>
                      {team.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-3">E-mail pozvaného</label>
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  className="w-full p-4 bg-white rounded-2xl border-2 border-gray-200 focus:outline-none focus:border-secondary-500 focus:ring-4 focus:ring-secondary-100 transition-all"
                  placeholder="hrac@example.com"
                  required
                />
              </div>
              <div className="md:col-span-2">
                <button
                  type="submit"
                  className="w-full py-4 font-bold text-white text-lg bg-gradient-to-r from-accent-500 to-accent-600 rounded-2xl shadow-lg hover:shadow-glow transform transition-all duration-200"
                >
                  Odeslat pozvánku
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="space-y-4">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">
            {teams.length > 0 ? 'Vaše týmy' : 'Zatím žádné týmy'}
          </h3>
          {teams.length === 0 ? (
            <div className="bg-white/60 backdrop-blur-sm p-8 sm:p-12 rounded-3xl text-center border border-white/50">
              <p className="text-gray-600 text-lg">Vytvořte svůj první tým a začněte!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              {teams.map((team, index) => (
                <div
                  key={team.id}
                  className="bg-white/80 backdrop-blur-lg p-6 rounded-3xl shadow-card border border-white hover:shadow-card-hover transform transition-all duration-200"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div>
                        <h3 className="text-xl font-bold text-gray-900">{team.name}</h3>
                        <p className="text-sm text-gray-600">ID týmu: {team.id}</p>
                      </div>
                    </div>
                    <div className="bg-gradient-to-r from-primary-500 to-secondary-500 text-white px-4 py-2 rounded-xl text-sm font-bold">
                      Aktivní
                    </div>
                  </div>
                  <button
                    onClick={() => fetchTeamDetails(team.id)}
                    className="mt-4 w-full py-2 bg-gray-50 text-gray-600 font-semibold rounded-xl hover:bg-gray-100 transition-colors flex items-center justify-center"
                  >
                    Zobrazit detaily
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">Příchozí pozvánky</h3>
          {invites.length === 0 ? (
            <div className="bg-white/60 backdrop-blur-sm p-8 rounded-3xl border border-white/50 text-center text-gray-600">
              Momentálně žádné čekající pozvánky.
            </div>
          ) : (
            <div className="space-y-4">
              {invites.map((invite) => (
                <div key={invite.id} className="bg-white/80 backdrop-blur-lg p-6 rounded-3xl shadow-card border border-white flex flex-col sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="text-lg font-bold text-gray-900">
                      {invite.team?.name}
                    </p>
                    <p className="text-sm text-gray-600">
                      Pozván od {invite.inviter?.name || invite.inviter?.email} dne {new Date(invite.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex items-center space-x-3 mt-4 sm:mt-0">
                    <button
                      onClick={() => handleAcceptInvite(invite.id)}
                      className="px-4 py-2 bg-green-500/90 text-white rounded-2xl font-semibold hover:bg-green-600 transition-colors"
                    >
                      Přijmout
                    </button>
                    <button
                      onClick={() => handleDeclineInvite(invite.id)}
                      className="px-4 py-2 bg-red-500/80 text-white rounded-2xl font-semibold hover:bg-red-600 transition-colors"
                    >
                      Odmítnout
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {isModalOpen && selectedTeam && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden">
            <div className="bg-gradient-to-r from-primary-500 to-secondary-500 p-6 text-white flex justify-between items-center">
              <h3 className="text-2xl font-bold">{selectedTeam.name}</h3>
              <button
                onClick={closeModal}
                className="p-2 hover:bg-white/20 rounded-full transition-colors"
              >
                ✕
              </button>
            </div>

            <div className="p-6">
              <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                Členové týmu
              </h4>

              <div className="space-y-3 mb-6">
                {selectedTeam.users?.map((member: TeamMember) => (
                  <div key={member.userId} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-primary-100 to-secondary-100 rounded-full flex items-center justify-center text-xl">
                        👤
                      </div>
                      <div>
                        <div className="font-bold text-gray-900">{member.user.name || 'Bezejmenný hráč'}</div>
                        <div className="text-xs text-gray-500">{member.user.email}</div>
                      </div>
                    </div>
                    {/* Future: Add remove button for captain */}
                  </div>
                ))}
              </div>

              <div className="flex justify-end">
                <button
                  onClick={closeModal}
                  className="px-6 py-2 bg-gray-100 text-gray-700 font-bold rounded-xl hover:bg-gray-200 transition-colors"
                >
                  Zavřít
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Teams;
